/**
 * Photo framing geometry, mirrored from aamcirc/media/framing.py (names and numbers match).
 * A box is [x0, y0, x1, y1] and a focal point [x, y], both fractions (0..1) of an image.
 */
import type { FeaturedFramingBody } from './framingApi'

export type Box = [number, number, number, number]
export type FitKey = 'card' | 'thumb' | 'social'
/** Each slot's fit; a slot left out is cropped (cover). */
export type Fit = Partial<Record<FitKey, 'cover' | 'contain'>>

export const FIT_RATIOS: Record<FitKey, number> = { card: 16 / 9, thumb: 3 / 2, social: 1.91 }

export const EPS = 0.005
/** Letterbox a slot only when its best cover crop shows less than this share of the subject box. */
export const KEEP_SHARE = 0.85

export const PREVIEW_SLOTS: { key: 'card' | 'lead' | 'thumb' | 'social' | 'desk'; label: string; fitKey: FitKey }[] = [
  { key: 'card', label: 'Card', fitKey: 'card' },
  { key: 'lead', label: 'Section lead', fitKey: 'card' },
  { key: 'thumb', label: 'List thumbnail', fitKey: 'thumb' },
  { key: 'social', label: 'Social share', fitKey: 'social' },
  { key: 'desk', label: 'Desk hero', fitKey: 'card' },
]

/** [cw, ch]: the visible fraction of each axis under object-fit: cover. */
function extents(w: number, h: number, ratio: number): [number, number] {
  const a = w / h
  return a > ratio ? [ratio / a, 1] : [1, a / ratio]
}

/** The visible part under `object-fit: cover; object-position: fx% fy%`. */
export function windowFor(w: number, h: number, ratio: number, focal: [number, number]): Box {
  const [cw, ch] = extents(w, h, ratio)
  const left = focal[0] * (1 - cw)
  const top = focal[1] * (1 - ch)
  return [left, top, left + cw, top + ch]
}

/** True when the box is no larger than the window on both axes. */
export function fitsSomewhere(w: number, h: number, ratio: number, box: Box): boolean {
  const [cw, ch] = extents(w, h, ratio)
  return box[2] - box[0] <= cw + EPS && box[3] - box[1] <= ch + EPS
}

/** The largest share of the subject box a cover crop of this ratio can show; 1 without dimensions. */
export function keptShare(w: number | null | undefined, h: number | null | undefined, ratio: number, box: Box): number {
  if (!w || !h) return 1
  const c = extents(w, h, ratio)
  let share = 1
  for (let a = 0; a < 2; a++) {
    const extent = box[a + 2] - box[a]
    if (extent > 0) share *= Math.min(1, c[a] / extent)
  }
  return share
}

/** True when the window contains the box. */
export function keeps(w: number, h: number, ratio: number, focal: [number, number], box: Box): boolean {
  const win = windowFor(w, h, ratio, focal)
  return box[0] >= win[0] - EPS && box[1] >= win[1] - EPS && box[2] <= win[2] + EPS && box[3] <= win[3] + EPS
}

const pct = (v: number): number => Math.round(v * 100 * 100) / 100

/** The CSS object-position value ("25% 33.33%"), as the reader's slotImage writes it. */
export function objectPosition(focal: [number, number] | null): string | undefined {
  return focal == null ? undefined : `${pct(focal[0])}% ${pct(focal[1])}%`
}

const clamp01 = (v: number): number => Math.min(Math.max(v, 0), 1)

/** A point of the full image as fractions of `crop`, clamped to 0..1. */
export function pointIntoCrop(p: [number, number], crop: Box): [number, number] {
  const cw = crop[2] - crop[0]
  const ch = crop[3] - crop[1]
  return [clamp01(cw > 0 ? (p[0] - crop[0]) / cw : 0.5), clamp01(ch > 0 ? (p[1] - crop[1]) / ch : 0.5)]
}

/** A point given as fractions of `crop`, as fractions of the full image. */
export function pointOutOfCrop(p: [number, number], crop: Box): [number, number] {
  return [crop[0] + p[0] * (crop[2] - crop[0]), crop[1] + p[1] * (crop[3] - crop[1])]
}

/** A box given as fractions of `crop`, as fractions of the full image. */
export function boxOutOfCrop(box: Box, crop: Box): Box {
  const cw = crop[2] - crop[0]
  const ch = crop[3] - crop[1]
  return [crop[0] + box[0] * cw, crop[1] + box[1] * ch, crop[0] + box[2] * cw, crop[1] + box[3] * ch]
}

/** The share of the box inside the window, as keptShare measures it (per axis, multiplied). */
function windowShare(win: Box, box: Box): number {
  let share = 1
  for (let a = 0; a < 2; a++) {
    const extent = box[a + 2] - box[a]
    if (extent > 0) share *= Math.max(0, Math.min(win[a + 2], box[a + 2]) - Math.max(win[a], box[a])) / extent
  }
  return share
}

export type SlotWarning = 'cannot_fit' | 'cuts_subject'

/**
 * Each fit's warning for this framing, as the server and the reader judge it:
 * - 'cannot_fit': the slot's best cover crop keeps less than KEEP_SHARE of the box (it would
 *   letterbox, so the desk offers Show whole);
 * - 'cuts_subject': it can keep that much, but the window at this focal point (null is the centre)
 *   keeps less of the box than the best one would.
 * A slot set to 'contain' shows the photo whole and has no warning; without a box or the
 * dimensions there is nothing to judge. Coordinates are fractions of the photo shown.
 */
export function slotWarnings(
  w: number | null | undefined, h: number | null | undefined, focal: [number, number] | null,
  box: Box | null, fit: Partial<Record<FitKey, string>> | null,
): Partial<Record<FitKey, SlotWarning>> {
  const out: Partial<Record<FitKey, SlotWarning>> = {}
  if (!w || !h || !box) return out
  for (const key of Object.keys(FIT_RATIOS) as FitKey[]) {
    if (fit?.[key] === 'contain') continue
    const ratio = FIT_RATIOS[key]
    const best = keptShare(w, h, ratio, box)
    if (best < KEEP_SHARE) out[key] = 'cannot_fit'
    else if (windowShare(windowFor(w, h, ratio, focal ?? [0.5, 0.5]), box) < best - EPS) out[key] = 'cuts_subject'
  }
  return out
}

/** The box at `ratio` (pixel width over height; null is free), shrunk about its centre and moved
 *  back inside the image. */
export function ratioLockBox(box: Box, ratio: number | null, w: number, h: number): Box {
  let [x0, y0, x1, y1] = box
  if (ratio != null && w > 0 && h > 0) {
    const bw = Math.min(x1 - x0, 1) * w
    const bh = Math.min(y1 - y0, 1) * h
    const cx = (x0 + x1) / 2
    const cy = (y0 + y1) / 2
    const [nw, nh] = bw / bh > ratio ? [bh * ratio, bh] : [bw, bw / ratio]
    x0 = cx - nw / w / 2; x1 = cx + nw / w / 2
    y0 = cy - nh / h / 2; y1 = cy + nh / h / 2
    // Shift (never resize) back inside: the shrunk box is no larger than the image.
    const dx = x0 < 0 ? -x0 : x1 > 1 ? 1 - x1 : 0
    const dy = y0 < 0 ? -y0 : y1 > 1 ? 1 - y1 : 0
    return [x0 + dx, y0 + dy, x1 + dx, y1 + dy].map(clamp01) as Box
  }
  return [clamp01(x0), clamp01(y0), clamp01(x1), clamp01(y1)]
}

/** framing.py `auto_fit`: 'contain' for each slot whose best cover crop keeps under KEEP_SHARE of
 *  the box; null when there is none, or no box or dimensions to judge by. */
export function autoFit(w: number | null | undefined, h: number | null | undefined, box: Box | null): Fit | null {
  if (!box || !w || !h) return null
  const out: Fit = {}
  for (const key of Object.keys(FIT_RATIOS) as FitKey[]) {
    if (keptShare(w, h, FIT_RATIOS[key], box) < KEEP_SHARE) out[key] = 'contain'
  }
  return Object.keys(out).length ? out : null
}

/** framing.py `clean_fit`: only known slots and values; empty is null. */
export function cleanFit(raw: Record<string, string> | null | undefined): Fit | null {
  const out: Fit = {}
  for (const [k, v] of Object.entries(raw ?? {})) {
    if (k in FIT_RATIOS && (v === 'cover' || v === 'contain')) out[k as FitKey] = v
  }
  return Object.keys(out).length ? out : null
}

/** The fit the reader uses, as framing.py `resolve` finds it: the piece's own when it has one,
 *  else the automatic letterbox for this photo (w, h and box are of the photo shown). */
export function effectiveFit(pieceFit: Fit | null, w: number | null | undefined, h: number | null | undefined, box: Box | null): Fit | null {
  return cleanFit(pieceFit) ?? autoFit(w, h, box)
}

/** `effective` with one slot set: what "Show whole" or "Crop to fill" saves, so the other slots
 *  keep the fit they show (an automatic letterbox included). */
export function withSlotFit(effective: Fit | null, key: FitKey, value: 'cover' | 'contain'): Fit {
  return { ...(effective ?? {}), [key]: value }
}

export const sameBox = (a: Box | null, b: Box | null): boolean =>
  a === b || (a != null && b != null && a.every((v, i) => Math.abs(v - b[i]) < 1e-4))

/** The dialog's featured-photo state, in fractions of the root original. */
export type FeaturedState = {
  /** The editor's own focal point (null: none set; a suggestion is never saved). */
  focal: [number, number] | null
  /** The piece's own subject box (a suggested box is never saved as the piece's). */
  pieceBox: Box | null
  /** The piece's own fit, as edited (null: the automatic letterbox). */
  fit: Fit | null
  trimOn: boolean
  trim: Box
  /** The trim the photo in use was cut with, or null for the original. */
  savedCrop: Box | null
}

/** The featured-framing save: the framing kept, plus a new trim when it changed, or the original
 *  back when the trim was turned off on a trimmed photo. The focal point is kept inside the trim. */
export function featuredSaveBody(s: FeaturedState): FeaturedFramingBody {
  const crop = s.trimOn ? s.trim : null
  const f = s.focal == null ? null
    : crop ? [Math.min(Math.max(s.focal[0], crop[0]), crop[2]), Math.min(Math.max(s.focal[1], crop[1]), crop[3])]
      : [clamp01(s.focal[0]), clamp01(s.focal[1])]
  const body: FeaturedFramingBody = {
    focal_x: f ? f[0] : null,
    focal_y: f ? f[1] : null,
    subject_box: s.pieceBox,
    fit: cleanFit(s.fit),
  }
  if (s.trimOn && !sameBox(s.trim, s.savedCrop)) body.crop_box = s.trim
  else if (!s.trimOn && s.savedCrop) body.restore_original = true
  return body
}

/** After "Reset to suggestion": the editor's own focal point and fit dropped (the suggestion and
 *  the automatic letterbox show again), even when they were never saved. The trim is left alone. */
export function resetFraming(s: FeaturedState): FeaturedState {
  return { ...s, focal: null, fit: null }
}

/** "Restore original": the framing kept, on the original photograph. */
export function restoreOriginalBody(s: FeaturedState): FeaturedFramingBody {
  const body = featuredSaveBody(s)
  delete body.crop_box
  body.restore_original = true
  return body
}
